/** Where mapbox parks a blocker overlay: `scrollZoom` holds its own, `dragPan` delegates the
 *  touch-pan blocker to the inner `_touchPan` handler. */
interface BlockerOverlayOwner {
  _alertContainer?: HTMLElement | undefined;
}

interface GestureHandler extends BlockerOverlayOwner {
  isEnabled(): boolean;
  enable(): void;
  disable(): void;
  _touchPan?: BlockerOverlayOwner | undefined;
}

/** The slice of mapbox's `Map` the toggle touches, narrow enough for a story to fake. */
export interface CooperativeGesturesMap {
  getCooperativeGestures(): boolean;
  setCooperativeGestures(enabled: boolean): unknown;
  scrollZoom: GestureHandler;
  dragPan: GestureHandler;
}

/**
 * Releases an overlay mapbox has already detached, so the next `enable()` builds a fresh one.
 *
 * mapbox (3.20.0) drops the node out of the DOM in `disable()` (`this._alertContainer.remove()`) but
 * keeps the field pointing at it, while `_addScrollZoomBlocker`/`_addTouchPanBlocker` early-return
 * `if (this._map && !this._alertContainer)`. Left alone, the second and every later off→on reuses a
 * node that hangs outside the DOM, so the hint silently never shows again for the session.
 *
 * Staleness is `parentNode`, not `isConnected`: the map is a singleton teleported between pages, and
 * an overlay still parented to a temporarily unmounted container is live, not stale. Run this after
 * the flag flip — mapbox dereferences the field unhedged in `disable()` while the flag is on.
 */
function releaseDetachedBlocker(handler: GestureHandler): void {
  for (const owner of [handler, handler._touchPan]) {
    if (owner?._alertContainer?.parentNode === null) owner._alertContainer = undefined;
  }
}

/**
 * Toggles mapbox's cooperative gestures on a map that was constructed without them.
 *
 * `setCooperativeGestures` only flips a flag. mapbox (3.20.0) creates the blocker overlay — and the
 * `mapboxgl-touch-pan-blocker-override mapboxgl-scrollable-page` classes that let a one-finger drag
 * scroll the page — inside `scrollZoom.enable()` and `dragPan.enable()`, which ran at construction
 * while the flag was still false. Flipping the flag alone leaves the overlay node missing and the
 * first blocked wheel or one-finger drag dereferences it. Cycling both handlers around the flip is
 * what builds it, and the order is load-bearing: `disable()` runs under the previous value, so
 * off→on skips removing a node that was never created and on→off strips the overlay plus those
 * classes; `enable()` then runs under the new value, once any node it detached has been released.
 */
export function applyCooperativeGestures(map: CooperativeGesturesMap, enabled: boolean): void {
  if (map.getCooperativeGestures() === enabled) return;

  // A non-interactive map is built with every handler disabled — enabling one here would silently
  // make it interactive.
  const scrollZoomWasEnabled = map.scrollZoom.isEnabled();
  const dragPanWasEnabled = map.dragPan.isEnabled();

  if (scrollZoomWasEnabled) map.scrollZoom.disable();
  if (dragPanWasEnabled) map.dragPan.disable();
  map.setCooperativeGestures(enabled);
  releaseDetachedBlocker(map.scrollZoom);
  releaseDetachedBlocker(map.dragPan);
  if (scrollZoomWasEnabled) map.scrollZoom.enable();
  if (dragPanWasEnabled) map.dragPan.enable();
}
